/**
 * `GET /v1/entrada/organizacoes` — em quais organizações esta pessoa entra.
 *
 * ── A tela que fica entre a senha e o produto ─────────────────────────────
 *
 * O login passou a ser em dois passos: e-mail e senha primeiro, organização
 * depois. Esta rota é o que a segunda tela lê.
 *
 * ── É a ÚNICA rota que aceita token sem organização ───────────────────────
 *
 * Todo o resto de `/v1` exige o claim `tenant_id`, que é o que abre o contexto
 * de RLS. Aqui ele ainda não existe — a escolha é justamente o que está sendo
 * feita —, então o token prova só `(emissor, subject)`.
 *
 * O que impede isso de virar uma porta larga é o que a rota devolve: nome de
 * organização e papel da própria pessoa, e nada mais. Nenhum dado de cliente,
 * de conversa ou de negócio atravessa — esses continuam todos do outro lado do
 * contexto, e não há contexto aqui.
 *
 * ── Por que não é `/v1/me` ────────────────────────────────────────────────
 *
 * Porque `/v1/me` responde "quem sou eu NESTA organização" — papel, escopo,
 * flags, carteira. Ele precisa de uma organização escolhida para existir. Esta
 * pergunta é anterior a ele, e misturar as duas faria `/v1/me` ter um modo sem
 * tenant que toda tela teria de considerar.
 *
 * ── Por que "organização", e não "revenda" ────────────────────────────────
 *
 * A rota nasceu como `/v1/entrada/revendas` (PR #490). "Revenda" é a palavra
 * do primeiro mercado que o produto atendeu, e ela é falsa para uma clínica ou
 * um consultório — que é para onde o piloto vai. Caminho de URL e nome de tipo
 * saem no contrato que o cliente lê, então eles seguem a palavra da tela.
 *
 * Por dentro o tenant continua se chamando `revenda` em muito lugar
 * (`clientDaRevenda`, `core.revendas_da_pessoa`, `/v1/operacao/revendas`).
 * Isso é de propósito: a régua é o que ATRAVESSA o contrato, não o vocabulário
 * de quem mantém o código.
 */

import { z } from 'zod';
import { IdSchema } from './comum.js';

export const OrganizacaoDaPessoaSchema = z
  .object({
    id: IdSchema,
    slug: z.string(),
    nome: z.string(),
    /**
     * O papel NESTA organização. A mesma pessoa pode ser gestora numa e agente
     * noutra.
     */
    papel: z.string(),
    /**
     * `suspenso` aparece na lista, e não entra.
     *
     * Esconder seria pior: quem foi suspenso numa organização e continua ativo
     * em outra veria a primeira sumir sem explicação, e abriria um chamado que
     * a tela poderia ter respondido sozinha.
     */
    status: z.enum(['ativo', 'suspenso']),
  })
  .strict();

export type OrganizacaoDaPessoa = z.infer<typeof OrganizacaoDaPessoaSchema>;

export const OrganizacoesDaPessoaSchema = z
  .object({ itens: z.array(OrganizacaoDaPessoaSchema) })
  .strict();

export type OrganizacoesDaPessoa = z.infer<typeof OrganizacoesDaPessoaSchema>;
